import i18n from 'i18next'
import { LEVEL_OPTIONS } from '../../redux/actions/storyLevelAction'

/**
 * Level ranges for mapping unique_word_count to display label. This is now a
 * FALLBACK only — the backend's stored combined level (story_level) is
 * preferred. Kept for stories not yet backfilled with a story_level. Labels are `common` keys.
 */
export const LEVEL_RANGES = [
  { label: 'common:levels.beginner1.label', min: 0, max: 37 },
  { label: 'common:levels.beginner2.label', min: 38, max: 54 },
  { label: 'common:levels.intermediate1.label', min: 55, max: 84 },
  { label: 'common:levels.intermediate2.label', min: 85, max: 119 },
  { label: 'common:levels.advanced1.label', min: 120, max: 134 },
  { label: 'common:levels.advanced2.label', min: 135, max: 154 },
  { label: 'common:levels.master.label', min: 155, max: Number.POSITIVE_INFINITY },
]

/** Map a StoryDifficultyLevel enum (e.g. "INTERMEDIATE_1") to its display label. */
export function levelLabelFromEnum(storyLevel?: string | null): string | null {
  if (!storyLevel) return null
  const opt = LEVEL_OPTIONS.find((o: any) => o.value === storyLevel)
  return opt ? opt.label : null
}

// The server's English level names (getLevelDisplayName and the level categories), by their
// lowercased words, to their label keys.
const LEVEL_KEYS_BY_NAME: Record<string, string> = {
  'beginner 1': 'common:levels.beginner1.label',
  'beginner 2': 'common:levels.beginner2.label',
  'intermediate 1': 'common:levels.intermediate1.label',
  'intermediate 2': 'common:levels.intermediate2.label',
  'advanced 1': 'common:levels.advanced1.label',
  'advanced 2': 'common:levels.advanced2.label',
  master: 'common:levels.master.label',
  beginner: 'common:levelCategories.beginner',
  intermediate: 'common:levelCategories.intermediate',
  advanced: 'common:levelCategories.advanced',
}

const levelNameKey = (value?: string | null): string | null => {
  if (!value) return null
  const words = value.trim().toLowerCase().replace(/[_\s]+/g, ' ')
  return LEVEL_KEYS_BY_NAME[words] ?? null
}

/**
 * A level the server sent, in the interface language. Takes the level code (`INTERMEDIATE_1`) and
 * the server's English text for it (`level_display`, e.g. "Intermediate 1", or a coarse level such
 * as "intermediate"). Text it does not know comes back as the server sent it.
 */
export function localizedLevelLabel(code?: string | null, serverText?: string | null): string | null {
  const fromEnum = levelLabelFromEnum(code)
  if (fromEnum) return fromEnum
  const key = levelNameKey(serverText)
  if (key) return i18n.t(key)
  return serverText || null
}

/**
 * Which band a level label (from the helpers here, in the interface language) belongs to, for
 * colouring a badge. Compares with the translated labels, never with English text.
 */
export function levelLabelBand(label?: string | null): 'advanced' | 'intermediate' | 'other' {
  if (!label) return 'other'
  const is = (keys: string[]) => keys.some((key) => i18n.t(key) === label)
  if (is(['common:levels.advanced1.label', 'common:levels.advanced2.label', 'common:levelCategories.advanced'])) {
    return 'advanced'
  }
  if (
    is(['common:levels.intermediate1.label', 'common:levels.intermediate2.label', 'common:levelCategories.intermediate'])
  ) {
    return 'intermediate'
  }
  return 'other'
}

/** Fallback: derive a display label from unique_word_count. */
export function levelLabelFromWordCount(count: number): string | null {
  if (Number.isNaN(count)) return null
  const range = LEVEL_RANGES.find((r) => count >= r.min && count <= r.max)
  return range ? i18n.t(range.label) : null
}
